import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Lightbulb, ArrowRight, CheckCircle2, Puzzle as PuzzleIcon } from "lucide-react";
import api, { track } from "@/lib/api";
import ChessBoard from "@/components/ChessBoard";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

const THEMES = ["Mate em 1", "Mate em 2", "Garfo", "Cravada", "Ataque duplo", "Ataque descoberto", "Defesa", "Finais"];
const DIFF = ["Fácil", "Médio", "Difícil"];

export default function Puzzles() {
  const { user } = useAuth();
  const [puzzles, setPuzzles] = useState([]);
  const [idx, setIdx] = useState(0);
  const [theme, setTheme] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [status, setStatus] = useState(null);
  const [hint, setHint] = useState("");
  const [wrongCount, setWrongCount] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const [explanation, setExplanation] = useState("");

  const load = useCallback(() => {
    const params = {};
    if (theme) params.theme = theme;
    if (difficulty) params.difficulty = difficulty;
    api.get("/puzzles", { params }).then(({ data }) => { setPuzzles(data); setIdx(0); resetState(); }).catch(() => {});
  }, [theme, difficulty]);
  useEffect(() => { load(); }, [load]);

  const resetState = () => { setStatus(null); setHint(""); setWrongCount(0); setExplanation(""); setResetKey((k) => k + 1); };

  const puzzle = puzzles[idx];

  const onMove = async (mv) => {
    if (!user) return;
    track("puzzle_started", "puzzle", { id: puzzle.id });
    try {
      const { data } = await api.post(`/puzzles/${puzzle.id}/attempt`, { move: mv.lan });
      if (data.correct) {
        setStatus("correct");
        setExplanation(data.explanation || "");
        if (data.xp_awarded > 0) toast.success(`Resolvido! +${data.xp_awarded} XP`);
        else toast.success("Correto!");
        track("puzzle_completed", "puzzle", { id: puzzle.id });
      } else {
        setStatus("wrong");
        setWrongCount((c) => c + 1);
        setTimeout(() => { setResetKey((k) => k + 1); setStatus(null); }, 900);
      }
    } catch { toast.error("Não foi possível verificar sua jogada. Tente novamente."); }
  };

  const getHint = async () => {
    try { const { data } = await api.get(`/puzzles/${puzzle.id}/hint`); setHint(data.hint); } catch { toast.error("Não foi possível carregar a dica. Tente novamente."); }
  };

  const next = () => { setIdx((i) => (i + 1) % puzzles.length); resetState(); };

  return (
    <div className="p-5 sm:p-8 max-w-5xl mx-auto">
      <h1 className="font-heading text-3xl text-chessup-text mb-1">Treinar Puzzles</h1>
      <p className="text-chessup-subtext text-sm mb-6">Resolva posições táticas e ganhe XP.</p>

      <div className="flex flex-wrap gap-2 mb-6">
        <select data-testid="puzzle-theme" value={theme} onChange={(e) => setTheme(e.target.value)} className="rounded-lg bg-chessup-deep border border-chessup-border px-3 py-2 text-sm text-chessup-text focus:border-chessup-turq focus:outline-none">
          <option value="">Todos os temas</option>
          {THEMES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select data-testid="puzzle-difficulty" value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className="rounded-lg bg-chessup-deep border border-chessup-border px-3 py-2 text-sm text-chessup-text focus:border-chessup-turq focus:outline-none">
          <option value="">Todas as dificuldades</option>
          {DIFF.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {!puzzle ? (
        <div className="rounded-2xl border border-chessup-border bg-chessup-card p-12 text-center">
          <PuzzleIcon className="h-10 w-10 text-chessup-subtext mx-auto mb-3" />
          <p className="text-chessup-text">Novos desafios estão chegando.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div className="rounded-2xl border border-chessup-border bg-chessup-deep/40 p-5">
            <ChessBoard fen={puzzle.fen} orientation={puzzle.fen.split(" ")[1] === "b" ? "black" : "white"} onMove={onMove} resetKey={resetKey} interactive={status !== "correct"} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="rounded-full bg-chessup-turq/12 border border-chessup-turq/30 px-3 py-1 text-xs text-chessup-turq">{puzzle.theme}</span>
              <span className="rounded-full bg-chessup-gold/12 border border-chessup-gold/30 px-3 py-1 text-xs text-chessup-goldlight">{puzzle.difficulty}</span>
            </div>
            <h2 className="font-heading text-2xl text-chessup-text">{puzzle.title}</h2>
            <p className="text-chessup-subtext mt-2">{puzzle.instruction}</p>

            <div className="mt-5 min-h-[60px]">
              {status === "correct" && <div className="rounded-lg bg-chessup-turq/12 border border-chessup-turq/40 px-4 py-3 text-sm text-chessup-turq" data-testid="puzzle-correct"><CheckCircle2 className="inline h-4 w-4 mr-1.5" /> {explanation || "Resolvido com maestria!"}</div>}
              {status === "wrong" && <div className="rounded-lg bg-destructive/15 border border-destructive/40 px-4 py-3 text-sm text-red-200" data-testid="puzzle-wrong">Quase. Tente outra vez.</div>}
              {status !== "correct" && wrongCount >= 1 && (
                <div className="mt-3">
                  <button onClick={getHint} data-testid="puzzle-hint-btn" className="inline-flex items-center gap-1.5 text-sm text-chessup-goldlight hover:underline"><Lightbulb className="h-4 w-4" /> Quer uma dica?</button>
                  {hint && <p className="mt-2 text-sm text-chessup-subtext italic">{hint}</p>}
                </div>
              )}
            </div>

            <button onClick={next} data-testid="next-puzzle-btn" className="mt-4 inline-flex items-center gap-2 rounded-full bg-chessup-gold px-6 py-3 font-semibold text-chessup-deep hover:bg-chessup-goldlight">Próximo puzzle <ArrowRight className="h-4 w-4" /></button>

            {!user && <p className="mt-4 text-sm text-chessup-subtext"><Link to="/entrar" className="text-chessup-turq">Entre</Link> para salvar seu progresso.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
